<template>
  <div>
    <div v-if="!loading">
      <apexchart width="100%" height="320" type="area" :options="chartOptions" :series="series"/>
    </div>
    <div v-else>
      <b-skeleton-img style="height: 320px"></b-skeleton-img>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MonetaryTimeSeriesChart',
  props: {
    loading: { type: Boolean, default: false },
    points: { type: Array, default: () => [] },
    isDarkSkin: { type: Boolean, default: false },
  },
  computed: {
    labelColor() {
      return this.isDarkSkin ? '#b4b7bd' : '#2b7194';
    },
    // Only chart FAKE deposits when the backend actually sends them —
    // otherwise (older cached responses) skip the series entirely instead of
    // drawing a flat zero line + dead legend entry.
    hasFakeSeries() {
      return this.points.some(p => p && p.fakeDeposits != null);
    },
    series() {
      const deposits = this.points.map(p => Number((p.deposits || 0).toFixed(2)));
      const withdrawals = this.points.map(p => Number(Math.abs(p.withdrawals || 0).toFixed(2)));
      const series = [
        { name: this.$t('Deposits'), data: deposits },
        { name: this.$t('Withdrawals'), data: withdrawals },
      ];
      if (this.hasFakeSeries) {
        series.push({
          name: this.$t('Fake Deposits'),
          data: this.points.map(p => Number((p.fakeDeposits || 0).toFixed(2))),
        });
      }
      return series;
    },
    categories() {
      return this.points.map(p => p.date);
    },
    chartOptions() {
      return {
        chart: {
          type: 'area',
          background: 'transparent',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        theme: { mode: this.isDarkSkin ? 'dark' : 'light' },
        // FAKE deposits (when present) render as a secondary series: amber,
        // dashed, with a fainter fill — toggleable via the legend like any
        // other apexcharts series.
        colors: this.hasFakeSeries ? ['#10b981', '#ef4444', '#ff9f43'] : ['#10b981', '#ef4444'],
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2,
          dashArray: this.hasFakeSeries ? [0, 0, 6] : 0,
        },
        // A line/area needs ≥2 points to draw. For a 1- or 2-day range show
        // visible point markers so the value isn't invisible; hide them for
        // longer ranges (shown on hover) to keep the line clean.
        markers: {
          size: this.points.length <= 2 ? 5 : 0,
          strokeWidth: 0,
          hover: { size: 6 },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: this.hasFakeSeries ? [0.45, 0.45, 0.12] : 0.45,
            opacityTo: this.hasFakeSeries ? [0.05, 0.05, 0.02] : 0.05,
            stops: [0, 90, 100],
          },
        },
        grid: { borderColor: this.isDarkSkin ? '#3b4253' : '#e7eaf3', strokeDashArray: 4 },
        xaxis: {
          categories: this.categories,
          type: 'datetime',
          labels: { style: { colors: this.labelColor }, datetimeUTC: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: this.labelColor },
            formatter: val => new Intl.NumberFormat('en', { notation: 'compact' }).format(val),
          },
        },
        tooltip: {
          theme: this.isDarkSkin ? 'dark' : 'light',
          x: { format: 'dd MMM yyyy' },
          y: { formatter: val => new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(val) },
        },
        legend: { labels: { colors: this.labelColor } },
      };
    },
  },
};
</script>
